import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { theme } from '../../theme/theme'
import { BodyLarge, Title4 } from '../styles/Typography'
import { OnClickButton } from '../buttons/onclick/OnClickButton'
import { useSelectScreeningStatus } from '../../state/selectors'
import { Modal } from '../modals/Modal'
import { ScreeningStatus } from '@estercare/ester-shared'

interface ScreeningModalProps {
  onClose: () => void
}

export const ScreeningModal = ({ onClose }: ScreeningModalProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const screeningStatus = useSelectScreeningStatus()

  if (!screeningStatus) return null

  const getContent = () => {
    switch (screeningStatus) {
      case ScreeningStatus.NOT_STARTED:
        return {
          title: t('employee.yourHealth.promptStartTitle'),
          description: t('employee.yourHealth.promptStartDescription'),
          buttonText: t('employee.yourHealth.promptStartButton'),
        }
      case ScreeningStatus.INCOMPLETE:
        return {
          title: t('employee.yourHealth.promptContinueTitle'),
          description: t('employee.yourHealth.promptContinueDescription'),
          buttonText: t('employee.yourHealth.promptContinueButton'),
        }
      case ScreeningStatus.FOLLOW_UP_NOT_STARTED:
        return {
          title: t('employee.yourHealth.promptFollowUpStartTitle'),
          description: t('employee.yourHealth.promptFollowUpStartDescription'),
          buttonText: t('employee.yourHealth.promptFollowUpStartButton'),
        }
      case ScreeningStatus.FOLLOW_UP_INCOMPLETE:
        return {
          title: t('employee.yourHealth.promptFollowUpContinueTitle'),
          description: t('employee.yourHealth.promptFollowUpContinueDescription'),
          buttonText: t('employee.yourHealth.promptFollowUpContinueButton'),
        }
      default:
        return null
    }
  }

  const content = getContent()
  if (!content) return null

  const handleClick = () => {
    navigate('/portal/health-services/screening')
    onClose()
  }

  return (
    <Modal onClose={onClose} backgroundColor={theme.color.beigeLight} maxWidth="480px">
      <Content>
        <Title>{content.title}</Title>
        <Description>{content.description}</Description>
        <OnClickButton text={content.buttonText} onClick={handleClick} buttonStyle={{ width: '100%' }} />
      </Content>
    </Modal>
  )
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.medium}px;
  padding-top: ${theme.spacing.large}px;
`

const Title = styled(Title4)`
  margin-bottom: -${theme.spacing.xsmall}px;
`

const Description = styled(BodyLarge)`
  color: ${theme.color.grey4};
  margin-bottom: ${theme.spacing.small}px;
`
